import SafeHtml from '../../components/SafeHtml';
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./VideoPlayer.css";

const speeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const formatTime = (seconds = 0) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
const youtubeId = (source = "") =>
  String(source).match(/(?:youtu\.be\/|v=|embed\/)([^?&/]+)/)?.[1] || "";

export default function VideoPlayer({
  lecture = {},
  renderHtml = false,
  stateName,
  categoryName,
  subjectName,
  elapsed,
  resumeAt,
  lockedPreview,
  onProgress,
  onPrevious,
  onNext,
  onPreviewEnded,
}) {
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const previewEnded = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [showSpeed, setShowSpeed] = useState(false);
  const [showChapters, setShowChapters] = useState(false);
  const provider = String(lecture.video_source || "").toLowerCase();
  const isVimeo = provider === "vimeo";
  const isYoutube = provider === "youtube";
  const source = lecture.lecture_video || "";
  const native = source && !isVimeo && !isYoutube;
  const topics = Array.isArray(lecture.topics_list) ? lecture.topics_list : [];
  const send = useCallback(
    (method, value) => {
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      if (isVimeo) target.postMessage(JSON.stringify({ method, value }), "*");
      else if (isYoutube)
        target.postMessage(
          JSON.stringify({
            event: "command",
            func: method,
            args: value === undefined ? [] : [value],
          }),
          "*",
        );
    },
    [isVimeo, isYoutube],
  );
  const endPreview = useCallback(() => {
    if (previewEnded.current) return;
    previewEnded.current = true;
    setPlaying(false);
    setPosition(0);
    onProgress?.(0);
    if (native && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    } else {
      send(isYoutube ? "pauseVideo" : "pause");
      send(isYoutube ? "seekTo" : "setCurrentTime", 0);
    }
    onPreviewEnded?.();
  }, [native, isYoutube, onProgress, onPreviewEnded, send]);
  const updatePosition = useCallback(
    (seconds, total) => {
      if (lockedPreview && Math.floor(seconds) >= 10) return endPreview();
      setPosition(seconds);
      if (total) setDuration(total);
      onProgress?.(seconds);
    },
    [lockedPreview, endPreview, onProgress],
  );
  useEffect(() => {
    previewEnded.current = false;
    setPosition(lockedPreview ? 0 : Number(resumeAt || 0));
    setDuration(0);
    setPlaying(false);
  // Playback progress changes resumeAt; it must never grant another preview.
  // Unlocked resume seeking is applied when the media becomes ready.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lecture.lecture_id, lockedPreview]);
  const subscribeToPlayer = useCallback(() => {
    if (isVimeo) {
      ['timeupdate', 'play', 'pause'].forEach(event => send('addEventListener', event));
    } else if (isYoutube) {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: `lecture-${lecture.lecture_id}` }), '*');
      send('addEventListener', 'onStateChange');
    }
  }, [isVimeo, isYoutube, send, lecture.lecture_id]);
  useEffect(() => {
    const listener = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== (isVimeo ? 'https://player.vimeo.com' : 'https://www.youtube-nocookie.com')) return;
      let data;
      try {
        data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch (_) {
        return;
      }
      if (data?.event === 'ready' || data?.event === 'onReady') subscribeToPlayer();
      const started = (isVimeo && data?.event === 'play') ||
        (isYoutube && (data?.event === 'onStateChange' && Number(data.info) === 1));
      if (started && lockedPreview && previewEnded.current) {
        send(isYoutube ? 'pauseVideo' : 'pause');
        send(isYoutube ? 'seekTo' : 'setCurrentTime', 0);
        return;
      }
      if (isVimeo && data?.event === "timeupdate")
        updatePosition(
          Number(data.data?.seconds || 0),
          Number(data.data?.duration || 0),
        );
      if (isVimeo && data?.event === "play") setPlaying(true);
      if (isVimeo && data?.event === "pause") setPlaying(false);
      if (isYoutube && data?.event === "infoDelivery")
        updatePosition(
          Number(data.info?.currentTime || 0),
          Number(data.info?.duration || 0),
        );
      if (isYoutube && data?.event === "onStateChange")
        setPlaying(Number(data.info) === 1);
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [isVimeo, isYoutube, updatePosition, subscribeToPlayer, lockedPreview, send]);
  const playerReady = () => {
    subscribeToPlayer();
    if (!lockedPreview && resumeAt > 0)
      window.setTimeout(
        () => send(isYoutube ? "seekTo" : "setCurrentTime", Number(resumeAt)),
        400,
      );
  };
  const toggle = () => {
    if (lockedPreview && previewEnded.current) return onPreviewEnded?.();
    if (native) {
      const player = videoRef.current;
      if (!player) return;
      player.paused ? player.play().catch(() => {}) : player.pause();
    } else {
      send(
        playing
          ? isYoutube
            ? "pauseVideo"
            : "pause"
          : isYoutube
            ? "playVideo"
            : "play",
      );
      setPlaying((value) => !value);
    }
  };
  const seek = (next) => {
    if (lockedPreview) return;
    const value = Math.max(0, Math.min(duration || next, next));
    if (native && videoRef.current) videoRef.current.currentTime = value;
    else send(isYoutube ? "seekTo" : "setCurrentTime", value);
    setPosition(value);
  };
  const changeSpeed = (value) => {
    setSpeed(value);
    setShowSpeed(false);
    if (native && videoRef.current) videoRef.current.playbackRate = value;
    else send(isYoutube ? "setPlaybackRate" : "setPlaybackRate", value);
  };
  const fullscreen = () =>
    playerRef.current?.requestFullscreen?.().catch(() => {});
  const vimeoSrc = `https://player.vimeo.com/video/${lecture.lecture_file_id || ""}?api=1&player_id=lecture-${lecture.lecture_id}${lockedPreview ? '&controls=0' : ''}`;
  const youtubeSrc = `https://www.youtube-nocookie.com/embed/${youtubeId(source)}?enablejsapi=1&playsinline=1${lockedPreview ? '&controls=0&disablekb=1' : ''}`;
  return (
    <div className="lecture-page">
      <div className="lecture-main">
        <div className="breadcrumb">
          <span>{renderHtml ? <SafeHtml as="span" inline value={stateName || "Home"} /> : stateName || "Home"}</span>
          <i className="bi bi-chevron-right" />
          <span>{renderHtml ? <SafeHtml as="span" inline value={categoryName || "Lectures"} /> : categoryName || "Lectures"}</span>
          <i className="bi bi-chevron-right" />
          <strong>{renderHtml ? <SafeHtml as="span" inline value={subjectName || "Subject"} /> : subjectName || "Subject"}</strong>
        </div>
        <div className="qbank-card qbank-card--question">
          <div className="qbank-questionTop">
            <span className="qbank-progress">{`${lecture.lecture_index || 1} of ${lecture.total_lectures || 1}`}</span>
            <span className="qbank-timer lectureTimer">
              <i className="bi bi-clock" />
              {formatTime(elapsed ?? lecture.timer_seconds)}
            </span>
          </div>
        </div>
        <div className="lectureTitle">
          <h3>{renderHtml ? <SafeHtml as="span" inline value={lecture.name || "Lecture"} /> : lecture.name || "Lecture"}</h3>
        </div>
        <section className="video-player" ref={playerRef}>
          <div className="video-screen">
            {!source ? (
              <div className="d-flex h-100 align-items-center justify-content-center text-white">
                Video is not available for this lecture.
              </div>
            ) : isVimeo || isYoutube ? (
              <iframe
                ref={iframeRef}
                onLoad={playerReady}
                title={lecture.name || "Lecture video"}
                className="video-element"
                style={lockedPreview ? { pointerEvents: 'none' } : undefined}
                tabIndex={lockedPreview ? -1 : undefined}
                src={isVimeo ? vimeoSrc : youtubeSrc}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                aria-label={lecture.name || 'Lecture video'}
                ref={videoRef}
                className="video-element"
                src={source}
                onLoadedMetadata={() => {
                  setDuration(videoRef.current?.duration || 0);
                  videoRef.current.currentTime = lockedPreview
                    ? 0
                    : Number(resumeAt || 0);
                }}
                onTimeUpdate={() =>
                  updatePosition(
                    videoRef.current?.currentTime || 0,
                    videoRef.current?.duration || 0,
                  )
                }
                onPlay={() => {
                  if (lockedPreview && previewEnded.current) {
                    videoRef.current.pause();
                    videoRef.current.currentTime = 0;
                    onPreviewEnded?.();
                  } else setPlaying(true);
                }}
                onPause={() => setPlaying(false)}
                onClick={toggle}
              />
            )}
            <button
              type="button"
              className="fullscreen-button"
              onClick={fullscreen}
            >
              <i className="bi bi-fullscreen" />
            </button>
          </div>
          <div className="video-controls">
            <div className="control-wrapper">
              <button
                type="button"
                className="control-button"
                onClick={() => setShowSpeed((value) => !value)}
              >
                <i className="bi bi-speedometer2" />
                <span>{speed}x</span>
              </button>
              {showSpeed ? (
                <div className="speed-dropdown">
                  {speeds.map((item) => (
                    <button
                      type="button"
                      key={item}
                      className={speed === item ? "active" : ""}
                      onClick={() => changeSpeed(item)}
                    >
                      {item}x
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="control-button"
              disabled={lockedPreview || !duration}
              onClick={() => seek(position - 10)}
            >
              <i className="bi bi-arrow-counterclockwise" />
              <span>Back</span>
            </button>
            <button
              type="button"
              className="simple-control"
              disabled={!lecture.previous_lecture_id}
              onClick={onPrevious}
            >
              <i className="bi bi-skip-start-fill" />
            </button>
            <button type="button" className="main-play-button" aria-label={playing ? 'Pause lecture' : 'Play lecture'} onClick={toggle}>
              <i className={playing ? "bi bi-pause-fill" : "bi bi-play-fill"} />
            </button>
            <button
              type="button"
              className="simple-control"
              disabled={!lecture.next_lecture_id}
              onClick={onNext}
            >
              <i className="bi bi-skip-end-fill" />
            </button>
            <button
              type="button"
              className="control-button"
              disabled={lockedPreview || !duration}
              onClick={() => seek(position + 10)}
            >
              <i className="bi bi-arrow-clockwise" />
              <span>Forward</span>
            </button>
            <div className="control-wrapper">
              <button
                type="button"
                className="control-button"
                onClick={() => setShowChapters((value) => !value)}
              >
                <i className="bi bi-list-ul" />
                <span>Chapters</span>
                <small>{topics.length}</small>
              </button>
              {showChapters ? (
                <div className="chapter-dropdown">
                  <div className="chapter-title">Topics Covered</div>
                  {topics.length ? (
                    topics.map((topic) => (
                      <div className="chapter" key={topic.id}>
                        <span>{renderHtml ? <SafeHtml as="span" inline value={topic.title} /> : topic.title}</span>
                      </div>
                    ))
                  ) : (
                    <div className="chapter">
                      <span>No topic available</span>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
